import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save,
  RotateCcw,
  Sparkles,
  History,
  Eye,
  X,
  Code,
  FileText,
  ChevronDown,
} from 'lucide-react';
import { OpenRouterModelPicker } from '../components/OpenRouterModelPicker';

const API = process.env.REACT_APP_BACKEND_URL;

const PROMPT_KEYS = ['A', 'B', 'C', 'D', 'E', 'HOOKS', 'COVER_REWRITE'];

const VAR_SAMPLE = {
  topic: '情緒管理',
  audience: '30+ 香港女性上班族',
  target_audience: '30+ 香港女性上班族',
  funnel: 'traffic',
  language: '繁體中文',
  language_style: '書面語',
  title: '為什麼越努力越累',
  arc_direction: '先打痛點再對比差距最後畀行動',
  key_points: ['點1', '點2', '點3'],
  ip_positioning: '情緒教練，專門陪伴 30+ 女性處理職場壓力',
  bg_hint: '晨光透窗簾灑喺木桌上嘅茶杯',
  composition: 'close-up',
  ref_images_base64: '(參考圖已嵌入)',
  excluded_hooks: '- 記帳三年 還是月光 問題不在你\n- 為什麼我越理財 越窮',
  chosen_hook: '記帳三年 還是月光',
  formula: 'C3',
  layout: '大小型',
  design_rationale: '整體風格：低飽和奶茶系 + 襯線字 + 大量留白\n色彩：奶茶底 #F5E6D3；墨炭字 #2B2B2B\n字型：封面 Noto Serif TC bold',
  original_p1_html: '<!DOCTYPE html>...（原 p1 HTML 已省略）',
};

const AdminCarouselPrompts = () => {
  const [prompts, setPrompts] = useState([]);
  const [activeKey, setActiveKey] = useState('A');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewRendered, setPreviewRendered] = useState('');
  const [showVersions, setShowVersions] = useState(false);
  const [note, setNote] = useState('');
  const [config, setConfig] = useState({ fusion_cost_warning: '', confirm_system: '' });
  const [configDefaults, setConfigDefaults] = useState({});
  const [configSaving, setConfigSaving] = useState(false);

  const active = useMemo(
    () => prompts.find((p) => p.key === activeKey),
    [prompts, activeKey]
  );

  const loadList = async () => {
    const r = await axios.get(`${API}/api/carousel/admin/prompts`);
    setPrompts(r.data || []);
  };

  const loadDetail = async (key) => {
    const r = await axios.get(`${API}/api/carousel/admin/prompts/${key}`);
    setDetail(r.data);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadList();
        const c = await axios.get(`${API}/api/carousel/admin/config`);
        setConfig({
          fusion_cost_warning: c.data.fusion_cost_warning || '',
          confirm_system: c.data.confirm_system || '',
        });
        setConfigDefaults({
          fusion_cost_warning: c.data.default_fusion_cost_warning || '',
          confirm_system: c.data.default_confirm_system || '',
        });
      } catch (e) {
        toast.error('載入失敗');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      await axios.put(`${API}/api/carousel/admin/config`, config);
      toast.success('生成底圖設定已儲存');
    } catch (e) {
      toast.error('儲存失敗');
    } finally {
      setConfigSaving(false);
    }
  };

  useEffect(() => {
    if (activeKey) {
      loadDetail(activeKey).catch(() => toast.error('載入詳情失敗'));
      setNote('');
    }
  }, [activeKey]);

  const setField = (field, value) => {
    setPrompts((ps) =>
      ps.map((p) => (p.key === activeKey ? { ...p, [field]: value } : p))
    );
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/carousel/admin/prompts/${activeKey}`, {
        content: active.content,
        model: active.model,
        temperature: active.temperature,
        reasoning_effort: active.reasoning_effort ?? null,
        max_tokens: active.max_tokens ?? null,
        note: note || null,
      });
      toast.success('已儲存（舊版本保留於歷史）');
      setNote('');
      await loadList();
      await loadDetail(activeKey);
    } catch (e) {
      toast.error('儲存失敗：' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const restoreDefault = () => {
    if (!active || !detail) return;
    setPrompts((ps) =>
      ps.map((p) =>
        p.key === activeKey
          ? {
              ...p,
              content: detail.default_content,
              model: detail.default_model,
              temperature: detail.default_temperature,
              reasoning_effort: detail.default_reasoning_effort ?? p.reasoning_effort,
              max_tokens: detail.default_max_tokens ?? p.max_tokens,
            }
          : p
      )
    );
    toast.info('已填入預設（記得點儲存）');
  };

  const previewRender = async () => {
    if (!active) return;
    try {
      const vars = {};
      active.variables.forEach((v) => {
        vars[v] = VAR_SAMPLE[v] ?? `(${v})`;
      });
      const r = await axios.post(
        `${API}/api/carousel/admin/prompts/${activeKey}/preview`,
        vars
      );
      setPreviewRendered(r.data.rendered);
      setShowPreview(true);
    } catch (e) {
      toast.error('預覽失敗：' + (e.response?.data?.detail || e.message));
    }
  };

  const rollback = async (versionId) => {
    if (!window.confirm('確定回滾？當前版本會被自動歸檔。')) return;
    try {
      await axios.post(
        `${API}/api/carousel/admin/prompts/${activeKey}/rollback`,
        { version_id: versionId }
      );
      toast.success('已回滾');
      await loadList();
      await loadDetail(activeKey);
      setShowVersions(false);
    } catch (e) {
      toast.error('回滾失敗：' + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return <main className="flex-1 overflow-y-auto p-10 text-sm text-neutral-500">載入中...</main>;
  }

  return (
    <main className="flex-1 overflow-y-auto p-4 md:p-8" data-testid="admin-carousel-prompts-root">
      <div className="max-w-6xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-primary-500" strokeWidth={2} />
          <span className="label-micro text-primary-500">CAROUSEL · PROMPTS</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-neutral-900 mb-2">
          輪播 Prompt 管理
        </h1>
        <p className="text-sm text-neutral-500 max-w-3xl">
          編輯 3 個核心 Prompt，支援版本歷史（最多 10 版）、模型選擇與溫度調整。
          儲存後即時生效，無需重啟。
        </p>
      </header>

      {/* Fusion config card */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-6" data-testid="carousel-fusion-config">
        <h2 className="text-sm font-semibold text-neutral-900 mb-2">生成底圖設定</h2>
        <p className="text-xs text-neutral-500 mb-4">
          用戶進入「生成底圖」Screen 時看到嘅成本提示 + AI 確認時嘅 system prompt。
        </p>
        <div className="space-y-4">
          <div>
            <label className="label-micro text-neutral-600 mb-1 block">
              COST · WARNING · 用戶提示
            </label>
            <textarea
              value={config.fusion_cost_warning}
              onChange={(e) =>
                setConfig({ ...config, fusion_cost_warning: e.target.value })
              }
              rows={3}
              className="w-full bg-white border border-neutral-300 focus:border-primary-500 rounded-lg px-3 py-2 text-sm outline-none resize-y"
              data-testid="carousel-config-warning"
            />
          </div>
          <div>
            <label className="label-micro text-neutral-600 mb-1 block">
              CONFIRM · SYSTEM · AI 確認步驟 system prompt
            </label>
            <textarea
              value={config.confirm_system}
              onChange={(e) =>
                setConfig({ ...config, confirm_system: e.target.value })
              }
              rows={3}
              className="w-full bg-white border border-neutral-300 focus:border-primary-500 rounded-lg px-3 py-2 text-sm outline-none resize-y font-mono text-xs"
              data-testid="carousel-config-confirm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfig(configDefaults)}
              className="inline-flex items-center gap-1.5 text-xs text-neutral-700 hover:text-neutral-900 px-3 py-2 rounded border border-neutral-300 bg-white"
            >
              還原預設
            </button>
            <button
              onClick={saveConfig}
              disabled={configSaving}
              className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 px-4 py-2 rounded disabled:opacity-60"
              data-testid="carousel-config-save"
            >
              <Save className="w-4 h-4" />
              {configSaving ? '儲存中...' : '儲存生成底圖設定'}
            </button>
          </div>
        </div>
      </div>

      {/* Prompt tabs */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="carousel-prompt-tabs">
        {PROMPT_KEYS.map((k) => {
          const p = prompts.find((x) => x.key === k);
          return (
            <button
              key={k}
              data-testid={`carousel-prompt-tab-${k}`}
              onClick={() => setActiveKey(k)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                activeKey === k
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:border-primary-300'
              }`}
            >
              <span className="font-mono text-xs opacity-70 mr-1">{k}</span>
              {p?.name?.replace(`Prompt ${k} · `, '') || `Prompt ${k}`}
            </button>
          );
        })}
      </div>

      {active && (
        <div className="space-y-5">
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
            <div className="text-xs text-primary-700 font-semibold mb-1">
              {active.name}
            </div>
            <p className="text-xs text-primary-800/80">{active.description}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {active.variables.map((v) => (
                <code
                  key={v}
                  className="text-[11px] bg-white border border-primary-300 text-primary-700 px-2 py-0.5 rounded"
                >{`{{${v}}}`}</code>
              ))}
            </div>
          </div>

          {/* Model + Temperature + Reasoning + Max tokens */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <label className="label-micro text-neutral-600 mb-2 block">
                MODEL
              </label>
              <OpenRouterModelPicker
                value={active.model || ''}
                onChange={(v) => setField('model', v)}
                mode={activeKey === 'C' ? 'image' : 'text'}
                defaultModel={active.default_model}
                testIdPrefix={`carousel-prompt-model-${activeKey}`}
              />
            </div>
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <label className="label-micro text-neutral-600 mb-2 block">
                TEMPERATURE {active.temperature ?? '(none)'}
              </label>
              {activeKey === 'C' ? (
                <div className="text-xs text-neutral-400 italic">
                  Gemini 圖像模型不使用 temperature
                </div>
              ) : (
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={active.temperature ?? 0.7}
                  onChange={(e) =>
                    setField('temperature', parseFloat(e.target.value))
                  }
                  className="w-full accent-primary-500"
                  data-testid={`carousel-prompt-temp-${activeKey}`}
                />
              )}
            </div>
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <label className="label-micro text-neutral-600 mb-2 block">
                REASONING · EFFORT
              </label>
              {activeKey === 'C' ? (
                <div className="text-xs text-neutral-400 italic">
                  Gemini 圖像模型不使用 reasoning
                </div>
              ) : (
                <>
                  <select
                    value={active.reasoning_effort || 'off'}
                    onChange={(e) =>
                      setField(
                        'reasoning_effort',
                        e.target.value === 'off' ? null : e.target.value
                      )
                    }
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                    data-testid={`carousel-prompt-reasoning-${activeKey}`}
                  >
                    <option value="off">關閉（無推理）</option>
                    <option value="low">LOW · 2K budget / effort:low</option>
                    <option value="medium">MEDIUM · 6K budget / effort:medium</option>
                    <option value="high">HIGH · 12K budget / effort:high（推薦）</option>
                    <option value="xhigh">XHIGH · Opus 4.7+ 獨有，最深 reasoning</option>
                  </select>
                  <p className="text-[11px] text-neutral-500 mt-1.5">
                    僅 Opus / Sonnet 4.x 支援。預設：
                    <code className="bg-neutral-100 px-1 rounded ml-1">
                      {detail?.default_reasoning_effort || 'off'}
                    </code>
                  </p>
                </>
              )}
            </div>
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <label className="label-micro text-neutral-600 mb-2 block">
                MAX · OUTPUT · TOKENS
              </label>
              <input
                type="number"
                min="500"
                max="60000"
                step="500"
                value={active.max_tokens ?? ''}
                placeholder={detail?.default_max_tokens || '16000'}
                onChange={(e) =>
                  setField(
                    'max_tokens',
                    e.target.value === '' ? null : parseInt(e.target.value, 10)
                  )
                }
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm tabular-nums"
                data-testid={`carousel-prompt-maxtokens-${activeKey}`}
              />
              <p className="text-[11px] text-neutral-500 mt-1.5">
                留空 = 用預設（
                <code className="bg-neutral-100 px-1 rounded">
                  {detail?.default_max_tokens || '16000'}
                </code>
                ）。此值為 <b>輸出</b> token 上限，推理預算另計。
              </p>
            </div>
          </div>

          {/* Prompt content */}
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="label-micro text-neutral-600">PROMPT · 內容</label>
              <div className="flex gap-2">
                <button
                  onClick={previewRender}
                  className="inline-flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 px-2 py-1 rounded border border-primary-200"
                  data-testid={`carousel-prompt-preview-${activeKey}`}
                >
                  <Eye className="w-3.5 h-3.5" /> 預覽（填入假數據）
                </button>
                <button
                  onClick={() => setShowVersions(true)}
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-600 hover:text-neutral-900 px-2 py-1 rounded border border-neutral-300"
                  data-testid={`carousel-prompt-history-${activeKey}`}
                >
                  <History className="w-3.5 h-3.5" /> 版本歷史（
                  {detail?.versions?.length || 0}）
                </button>
              </div>
            </div>
            <textarea
              value={active.content}
              onChange={(e) => setField('content', e.target.value)}
              rows={26}
              className="w-full bg-neutral-50 border border-neutral-300 focus:border-primary-500 rounded-lg px-4 py-3 text-[13px] leading-relaxed font-mono outline-none resize-y"
              data-testid={`carousel-prompt-content-${activeKey}`}
            />
          </div>

          {/* Note + actions */}
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <label className="label-micro text-neutral-600 mb-2 block">
              版本備註（選填）
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例如：加強金句密度"
              className="w-full bg-white border border-neutral-300 focus:border-primary-500 rounded-lg px-3 py-2 text-sm outline-none mb-3"
              data-testid={`carousel-prompt-note-${activeKey}`}
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-neutral-500">
                {active.updated_at
                  ? `最後更新：${new Date(active.updated_at).toLocaleString()} by ${active.updated_by || '—'}`
                  : '尚未儲存過（使用預設）'}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={restoreDefault}
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-700 hover:text-neutral-900 px-3 py-2 rounded border border-neutral-300 bg-white"
                  data-testid={`carousel-prompt-restore-${activeKey}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" /> 還原預設
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 px-4 py-2 rounded disabled:opacity-60"
                  data-testid={`carousel-prompt-save-${activeKey}`}
                >
                  <Save className="w-4 h-4" />
                  {saving ? '儲存中...' : '儲存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
          data-testid="carousel-preview-modal"
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-primary-500" />
                <h3 className="font-semibold text-neutral-900">
                  Prompt 預覽（變數已填入假數據）
                </h3>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1.5 hover:bg-neutral-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-5 text-xs font-mono whitespace-pre-wrap bg-neutral-900 text-neutral-100">
              {previewRendered}
            </pre>
          </div>
        </div>
      )}

      {/* Version history */}
      {showVersions && detail && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowVersions(false)}
          data-testid="carousel-versions-modal"
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-primary-500" />
                <h3 className="font-semibold text-neutral-900">
                  版本歷史 · Prompt {activeKey}
                </h3>
              </div>
              <button
                onClick={() => setShowVersions(false)}
                className="p-1.5 hover:bg-neutral-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {detail.versions.length === 0 && (
                <div className="text-center text-sm text-neutral-500 py-12">
                  暫無歷史版本
                </div>
              )}
              {detail.versions.map((v) => (
                <details
                  key={v.id}
                  className="border border-neutral-200 rounded-lg overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-3 bg-neutral-50 cursor-pointer list-none">
                    <div className="flex items-center gap-3">
                      <ChevronDown className="w-4 h-4" />
                      <div>
                        <div className="text-sm text-neutral-900">
                          {new Date(v.created_at).toLocaleString()}
                        </div>
                        <div className="text-[11px] text-neutral-500">
                          {v.created_by || '—'} · {v.model}
                          {v.note ? ` · ${v.note}` : ''}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        rollback(v.id);
                      }}
                      className="text-xs bg-primary-500 hover:bg-primary-600 text-white px-3 py-1.5 rounded"
                      data-testid={`carousel-prompt-rollback-${v.id}`}
                    >
                      回滾至此版本
                    </button>
                  </summary>
                  <pre className="p-3 text-[11px] font-mono whitespace-pre-wrap bg-white border-t border-neutral-200 max-h-60 overflow-auto">
                    {v.content}
                  </pre>
                </details>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </main>
  );
};

export default AdminCarouselPrompts;
